/**
 * Spot WebSocket API
 *
 * Access market data, manage accounts, and trade on Binance Spot.
 *
 * The version of the OpenAPI document: 1.0.0
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { WebsocketAPIBase, WebsocketApiResponse, WebsocketSendMsgOptions } from '@binance/common';
import type { SessionLogonResponse, SessionLogoutResponse, SessionStatusResponse } from '../types';

/**
 * AuthApi - interface
 *
 * @interface AuthApi
 */
export interface AuthApiInterface {
    /**
     * Authenticate WebSocket connection using the provided API key.
     *
     * After calling `session.logon`, you can omit `apiKey` and `signature` parameters for future requests that require them.
     *
     * Note that only one API key can be authenticated.
     *
     * Calling `session.logon` multiple times changes the current authenticated API key.
     *
     **Note:** Only Ed25519 keys are supported for this feature.
     *
     * Weight(IP): 2
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Log in with API key (USER_DATA)
     * @param {SessionLogonRequest} requestParameters Request parameters.
     *
     * @returns {Promise<SessionLogonResponse>}
     * @memberof AuthApiInterface
     */
    sessionLogon(
        requestParameters?: SessionLogonRequest
    ): Promise<WebsocketApiResponse<SessionLogonResponse>[]>;

    /**
     * Forget the API key previously authenticated. If the connection is not authenticated, this request does nothing.
     *
     * Note that the WebSocket connection stays open after `session.logout` request. You can continue using the connection, but now you will have to explicitly provide the `apiKey` and `signature` parameters where needed.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Log out of the session
     * @param {SessionLogoutRequest} requestParameters Request parameters.
     *
     * @returns {Promise<SessionLogoutResponse>}
     * @memberof AuthApiInterface
     */
    sessionLogout(
        requestParameters?: SessionLogoutRequest
    ): Promise<WebsocketApiResponse<SessionLogoutResponse>[]>;

    /**
     * Query the status of the WebSocket connection,
     * inspecting which API key (if any) is used to authorize requests.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Query session status
     * @param {SessionStatusRequest} requestParameters Request parameters.
     *
     * @returns {Promise<SessionStatusResponse>}
     * @memberof AuthApiInterface
     */
    sessionStatus(
        requestParameters?: SessionStatusRequest
    ): Promise<WebsocketApiResponse<SessionStatusResponse>>;
}

/**
 * Request parameters for sessionLogon operation in AuthApi.
 * @interface SessionLogonRequest
 */
export interface SessionLogonRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AuthApiSessionLogon
     */
    readonly id?: string;

    /**
     * The value cannot be greater than `60000`. Supports up to three decimal places of precision (e.g., 6000.346) so that microseconds may be specified.
     * @type {number}
     * @memberof AuthApiSessionLogon
     */
    readonly recvWindow?: number;
}

/**
 * Request parameters for sessionLogout operation in AuthApi.
 * @interface SessionLogoutRequest
 */
export interface SessionLogoutRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AuthApiSessionLogout
     */
    readonly id?: string;
}

/**
 * Request parameters for sessionStatus operation in AuthApi.
 * @interface SessionStatusRequest
 */
export interface SessionStatusRequest {
    /**
     * Client-generated request identifier.
     * @type {string}
     * @memberof AuthApiSessionStatus
     */
    readonly id?: string;
}

/**
 * AuthApi - object-oriented interface
 * @class AuthApi
 * @extends {WebsocketAPIBase}
 */
export class AuthApi implements AuthApiInterface {
    private readonly websocketBase: WebsocketAPIBase;

    constructor(websocketBase: WebsocketAPIBase) {
        this.websocketBase = websocketBase;
    }

    /**
     * Authenticate WebSocket connection using the provided API key.
     *
     * After calling `session.logon`, you can omit `apiKey` and `signature` parameters for future requests that require them.
     *
     * Note that only one API key can be authenticated.
     *
     * Calling `session.logon` multiple times changes the current authenticated API key.
     *
     **Note:** Only Ed25519 keys are supported for this feature.
     *
     * Weight(IP): 2
     *
     * Security Type: USER_DATA
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Log in with API key (USER_DATA)
     * @param {SessionLogonRequest} requestParameters Request parameters.
     * @returns {Promise<SessionLogonResponse>}
     * @memberof AuthApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/auth#session-logon Binance API Documentation}
     */
    public sessionLogon(
        requestParameters: SessionLogonRequest = {}
    ): Promise<WebsocketApiResponse<SessionLogonResponse>[]> {
        return this.websocketBase.sendMessage<SessionLogonResponse>(
            '/session.logon'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: true, withApiKey: false, isSessionLogon: true }
        );
    }

    /**
     * Forget the API key previously authenticated. If the connection is not authenticated, this request does nothing.
     *
     * Note that the WebSocket connection stays open after `session.logout` request. You can continue using the connection, but now you will have to explicitly provide the `apiKey` and `signature` parameters where needed.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Log out of the session
     * @param {SessionLogoutRequest} requestParameters Request parameters.
     * @returns {Promise<SessionLogoutResponse>}
     * @memberof AuthApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/auth#session-logout Binance API Documentation}
     */
    public sessionLogout(
        requestParameters: SessionLogoutRequest = {}
    ): Promise<WebsocketApiResponse<SessionLogoutResponse>[]> {
        return this.websocketBase.sendMessage<SessionLogoutResponse>(
            '/session.logout'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: false, withApiKey: false, isSessionLogout: true }
        );
    }

    /**
     * Query the status of the WebSocket connection,
     * inspecting which API key (if any) is used to authorize requests.
     *
     * Weight(IP): 2
     *
     * Security Type: NONE
     *
     * Notes:
     **Data Source:** Memory
     *
     * @summary Query session status
     * @param {SessionStatusRequest} requestParameters Request parameters.
     * @returns {Promise<SessionStatusResponse>}
     * @memberof AuthApi
     * @see {@link https://developers.binance.com/en/docs/catalog/core-trading-spot-trading/api/ws-api/auth#session-status Binance API Documentation}
     */
    public sessionStatus(
        requestParameters: SessionStatusRequest = {}
    ): Promise<WebsocketApiResponse<SessionStatusResponse>> {
        return this.websocketBase.sendMessage<SessionStatusResponse>(
            '/session.status'.slice(1),
            requestParameters as unknown as WebsocketSendMsgOptions,
            { isSigned: false, withApiKey: false }
        );
    }
}
